/**
 * USR: Mi Perfil
 * Tabs: Cuenta (info + cambiar contraseña) · Notificaciones (preferencias).
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, Mail, KeyRound, ArrowLeft, Bell, Palette } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { ROLE_NAMES, type RoleType } from '@/types'
import { NotificationPreferencesTab } from './NotificationPreferencesTab'
import { ThemeToggle } from '@/components/ThemeToggle'
import { useThemeStore } from '@/store/themeStore'

type Tab = 'account' | 'notifications' | 'appearance'

export function ProfilePage() {
  const navigate = useNavigate()
  const { user, activeTenant } = useAuthStore()
  const [tab, setTab] = useState<Tab>('account')

  if (!user) return null

  const initials = (user.full_name || user.email || 'U')
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-1.5 rounded-lg text-content-subtle hover:text-content hover:bg-soft transition-colors"
          aria-label="Volver"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold text-content">Mi Perfil</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-divider mb-5">
        <TabButton active={tab === 'account'} onClick={() => setTab('account')} icon={<User size={14} />}>
          Cuenta
        </TabButton>
        <TabButton active={tab === 'notifications'} onClick={() => setTab('notifications')} icon={<Bell size={14} />}>
          Notificaciones
        </TabButton>
        <TabButton active={tab === 'appearance'} onClick={() => setTab('appearance')} icon={<Palette size={14} />}>
          Apariencia
        </TabButton>
      </div>

      {tab === 'account' && (
        <>
          {/* Avatar + nombre */}
          <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-6 mb-4 shadow-card">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center shrink-0">
                <span className="text-white text-xl font-bold">{initials}</span>
              </div>
              <div>
                <p className="text-lg font-semibold text-content">{user.full_name || 'Sin nombre'}</p>
                <p className="text-sm text-content-muted">{user.email}</p>
                {user.is_platform_admin && (
                  <span className="inline-block mt-1 px-2 py-0.5 bg-info-surface-strong text-info-content dark:bg-violet-900 dark:text-violet-200 text-xs font-medium rounded-full">
                    Super Administrador
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3 py-3 border-t border-divider">
                <User size={16} className="text-content-subtle shrink-0" />
                <div>
                  <p className="text-xs text-content-subtle font-medium">Nombre completo</p>
                  <p className="text-sm text-content">{user.full_name || '—'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 py-3 border-t border-divider">
                <Mail size={16} className="text-content-subtle shrink-0" />
                <div>
                  <p className="text-xs text-content-subtle font-medium">Email</p>
                  <p className="text-sm text-content">{user.email}</p>
                </div>
              </div>

              {activeTenant && (
                <div className="flex items-center gap-3 py-3 border-t border-divider">
                  <div className="w-4 h-4 rounded bg-surface-muted flex items-center justify-center shrink-0">
                    <span className="text-content-muted text-xs font-bold">E</span>
                  </div>
                  <div>
                    <p className="text-xs text-content-subtle font-medium">Empresa activa</p>
                    <p className="text-sm text-content">{activeTenant.name}</p>
                    {activeTenant.roles?.length > 0 && (
                      <p className="text-xs text-content-muted mt-0.5">
                        {activeTenant.roles.map(r => ROLE_NAMES[r as RoleType] || r).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Acciones */}
          <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-4 shadow-card">
            <button
              onClick={() => navigate('/change-password')}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-soft transition-colors text-left"
            >
              <KeyRound size={16} className="text-content-subtle shrink-0" />
              <div>
                <p className="text-sm font-medium text-content">Cambiar contraseña</p>
                <p className="text-xs text-content-muted">Actualiza tu contraseña de acceso</p>
              </div>
            </button>
          </div>
        </>
      )}

      {tab === 'notifications' && <NotificationPreferencesTab />}

      {tab === 'appearance' && <AppearanceSection />}
    </div>
  )
}

function AppearanceSection() {
  const { seasonalEnabled, setSeasonalEnabled, dismissedSeasons, clearDismissedSeasons } = useThemeStore()

  return (
    <div className="space-y-4">
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-6 shadow-card">
        <h2 className="text-base font-semibold text-content mb-1">Tema de la interfaz</h2>
        <p className="text-sm text-content-muted mb-4">
          Elige cómo se ve Clobi. <span className="font-medium">Sistema</span> sigue la
          preferencia de tu equipo (se adapta automáticamente si cambias entre modo
          claro/oscuro en tu sistema operativo).
        </p>
        <ThemeToggle />
      </div>

      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-6 shadow-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-base font-semibold text-content mb-1">Temas festivos</h2>
            <p className="text-sm text-content-muted">
              Muestra un banner decorativo sutil durante festividades como Navidad, Fiestas
              Patrias o Año Nuevo, según el país de tu empresa. Puedes descartar cada banner
              individualmente con la "X" cuando aparezca.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input
              type="checkbox"
              checked={seasonalEnabled}
              onChange={e => setSeasonalEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-divider-strong after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
          </label>
        </div>
        {dismissedSeasons.length > 0 && (
          <div className="mt-4 pt-4 border-t border-divider flex items-center justify-between gap-3">
            <p className="text-xs text-content-muted">
              Descartaste {dismissedSeasons.length} {dismissedSeasons.length === 1 ? 'tema' : 'temas'}.
            </p>
            <button
              type="button"
              onClick={clearDismissedSeasons}
              className="text-xs text-primary hover:text-primary-hover hover:underline"
            >
              Restaurar descartados
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function TabButton({
  active, onClick, icon, children,
}: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active
          ? 'border-primary text-primary'
          : 'border-transparent text-content-muted hover:text-content'
      }`}
    >
      {icon}
      {children}
    </button>
  )
}
